import { Injectable, inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { profileStore } from '@core/store';
import { AuthService } from './auth.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TokenValidationService {
  private readonly jwtHelper = inject(JwtHelperService);
  private readonly store = inject(profileStore);
  private readonly authService = inject(AuthService);
  
  // Tiempo mínimo antes de expiración para refrescar (5 minutos)
  private readonly REFRESH_THRESHOLD_MINUTES = 5;
  
  private isRefreshing = false;

  /**
   * Verifica si el token actual es válido
   */
  isTokenValid(): boolean {
    const token = this.store.data.token.value();
    
    if (!token || token === '') {
      return false;
    }

    try {
      return !this.jwtHelper.isTokenExpired(token);
    } catch {
      return false;
    }
  }

  /**
   * Verifica si el token está próximo a expirar
   */
  isTokenNearExpiration(): boolean {
    const token = this.store.data.token.value();
    
    if (!token || token === '') {
      return false;
    }

    try {
      const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
      if (!expirationDate) {
        return false;
      }

      const now = new Date();
      const timeUntilExpiration = expirationDate.getTime() - now.getTime();
      const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);

      return minutesUntilExpiration <= this.REFRESH_THRESHOLD_MINUTES && minutesUntilExpiration > 0;
    } catch {
      return false;
    }
  }

  /**
   * Obtiene el tiempo restante hasta la expiración en minutos
   */
  getTimeUntilExpiration(): number {
    const token = this.store.data.token.value();
    
    if (!token || token === '') {
      return 0;
    }

    try {
      const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
      if (!expirationDate) {
        return 0;
      }

      const now = new Date();
      const timeUntilExpiration = expirationDate.getTime() - now.getTime();
      return Math.max(0, timeUntilExpiration / (1000 * 60));
    } catch {
      return 0;
    }
  }

  /**
   * Intenta refrescar el token usando el refresh token
   */
  refreshTokenIfNeeded(): Observable<boolean> {
    // Si ya estamos refrescando, no hacer nada
    if (this.isRefreshing) {
      return of(true);
    }

    // Si el token es válido y no está cerca de expirar, no hacer nada
    if (this.isTokenValid() && !this.isTokenNearExpiration()) {
      return of(true);
    }

    // Si no hay refresh token, no podemos refrescar
    const refreshToken = this.store.data.token().refreshToken;
    if (!refreshToken) {
      return of(false);
    }

    this.isRefreshing = true;

    return this.authService.refreshToken(refreshToken).pipe(
      map(response => {
        this.isRefreshing = false;
        
        if (response.status === 1 && response.data) {
          // Actualizar token en el store
          this.store.setToken(response.data);
          return true;
        }
        
        return false;
      }),
      catchError(error => {
        this.isRefreshing = false;
        console.error('Error al refrescar token:', error);
        return of(false);
      })
    );
  }

  /**
   * Limpia el token y datos del usuario si es inválido
   */
  clearInvalidToken(): void {
    if (!this.isTokenValid()) {
      this.store.clearProfile();
    }
  }

  /**
   * Decodifica el token JWT actual
   */
  decodeToken(): any {
    const token = this.store.data.token.value();
    
    if (!token || token === '') {
      return null;
    }

    try {
      return this.jwtHelper.decodeToken(token);
    } catch {
      return null;
    }
  }

  /**
   * Verifica si el refresh token es válido
   */
  isRefreshTokenValid(): boolean {
    const tokenData = this.store.data.token();
    const refreshTokenExpire = tokenData.refreshTokenExpire;
    
    if (!refreshTokenExpire) {
      return false;
    }

    try {
      const expirationDate = new Date(refreshTokenExpire);
      const now = new Date();
      return expirationDate.getTime() > now.getTime();
    } catch {
      return false;
    }
  }
}

